import { motion } from "framer-motion";
import { BarChart3, ShieldCheck } from "lucide-react";
import {
  buildHorizonRows,
  buildStreamRows,
  buildTrajectoryRows,
  summarizeDataHealthOperatorLine,
} from "../lib/dataHealth";
import type { DataHealthResponse } from "../lib/types";

export function DataHealthPanel({
  response,
  failed = false,
}: {
  response?: DataHealthResponse;
  failed?: boolean;
}) {
  const line = summarizeDataHealthOperatorLine(response, { failed });
  const snapshot = response?.snapshot;
  const streamRows = buildStreamRows(snapshot);
  const horizonRows = buildHorizonRows(snapshot);
  const trajectoryRows = buildTrajectoryRows(response);

  return (
    <motion.section
      key="collection"
      className="tab-screen"
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -24 }}
      transition={{ duration: 0.24 }}
    >
      <section className="panel solo-panel">
        <div className="panel-head">
          <div>
            <p className="eyebrow">관측 전용</p>
            <h2>데이터 수집 &amp; 엣지 진전</h2>
          </div>
          <BarChart3 size={21} />
        </div>
        <section
          className={`source-health-strip source-health-${line.tone}`}
          aria-label="수집·엣지 요약"
        >
          <ShieldCheck size={18} aria-hidden="true" />
          <div>
            <strong>{line.title}</strong>
            <span>{line.counts}</span>
          </div>
          <p>{line.review}</p>
        </section>
        {snapshot?.verdict?.line ? <p className="muted-text">{snapshot.verdict.line}</p> : null}
        <div className="trade-tape-list">
          <strong>수집 스트림</strong>
          {streamRows.length ? (
            streamRows.map((row) => (
              <article className="trade-line" key={row.label}>
                <div className="trade-line-main">
                  <strong>{row.label}</strong>
                  <span className="trade-badge">{row.status}</span>
                </div>
                <span className="trade-side">
                  {row.ageLabel} · 오늘 {row.rowsToday}
                </span>
                <span className="trade-fee">{row.note}</span>
              </article>
            ))
          ) : (
            <p className="empty">스트림 스냅샷 없음</p>
          )}
        </div>
        <div className="trade-tape-list">
          <strong>오펜스 바 (Horizon)</strong>
          {horizonRows.length ? (
            horizonRows.map((row) => (
              <article className="trade-line" key={row.horizon}>
                <div className="trade-line-main">
                  <strong>{row.horizon}</strong>
                  <span className="trade-badge">
                    자격 {row.qualifiedCohorts} · n {row.n}
                  </span>
                </div>
                <span className="trade-side">
                  EV {row.evMeanBps} · LB {row.evLowerBps}
                </span>
              </article>
            ))
          ) : (
            <p className="empty">엣지 측정값 없음</p>
          )}
        </div>
        <div className="trade-tape-list">
          <strong>Trajectory (스냅샷 이력)</strong>
          {trajectoryRows.length ? (
            trajectoryRows.map((row) => (
              <article className="trade-line" key={row.id}>
                <div className="trade-line-main">
                  <strong>{row.observedAt}</strong>
                  <span className="trade-badge">
                    {row.topStatus} · bar {row.barMet}
                  </span>
                </div>
                <span className="trade-side">{row.horizons || "—"}</span>
              </article>
            ))
          ) : (
            <p className="empty">trajectory 기록 없음</p>
          )}
        </div>
        {snapshot?.corpusObservation?.observed ? (
          <p className="muted-text">
            코퍼스: {snapshot.corpusObservation.observed}
            {snapshot.corpusObservation.hypothesis
              ? ` · 가설: ${snapshot.corpusObservation.hypothesis}`
              : ""}
          </p>
        ) : null}
        {snapshot?.lanesNote?.observed ? (
          <p className="muted-text">레인: {snapshot.lanesNote.observed}</p>
        ) : null}
      </section>
    </motion.section>
  );
}
